import useIspmFeatureFlag from './useIspmFeatureFlag';
import useIntegrationFeatureFlag from './useIntegrationFeatureFlag';
import useUniversalOnboardingFeatureFlag from './useUniversalOnboardingFeatureFlag';
// Single source of truth for MFE config. The `featureFlag` field on each MFE
// entry maps it to one of the bespoke hooks below. This is the same file read by
// njs (mfe_version_resolver.js) and rootConfig.js (mfes.js) — see craco.config.js
// for the ModuleScopePlugin whitelist that allows importing it into src/.
import mfeConfig from '../../../../../public/ECMv6/assets/js/mfe/mfes.json';

/**
 * "Always enabled" flag hook for MFEs that have no feature gate today
 * (agent, agent-gateway, legacy-migration). Kept as a hook so every MFE goes
 * through the same code path and the Rules of Hooks are never violated.
 *
 * @returns {{isMfeEnabled: boolean, isLoading: boolean, isError: boolean, error: null}}
 */
const useAlwaysEnabled = () => ({
  isMfeEnabled: true,
  isLoading: false,
  isError: false,
  error: null,
});

/**
 * Registry mapping a mfes.json `featureFlag` value to its bespoke hook.
 * To gate a new MFE, set its `featureFlag` in mfes.json to one of these keys
 * (or add a new key here + its hook). No route/component changes needed.
 */
const FLAG_HOOKS = {
  ispm: useIspmFeatureFlag,
  integration: useIntegrationFeatureFlag,
  universalOnboarding: useUniversalOnboardingFeatureFlag,
  none: useAlwaysEnabled,
};

/**
 * Resolves and runs the feature-flag hook declared for an MFE in mfes.json.
 *
 * Rules of Hooks: `mfeId` is fixed for a given mounted route component, so the
 * resolved hook is stable across that component's renders — calling it here is
 * safe even though the hook identity is looked up dynamically.
 *
 * @param {string} mfeId - the MFE's key in mfes.json (e.g. "ispm", "integration")
 * @returns {{isMfeEnabled: boolean, isLoading: boolean, isError: boolean, error: (Error|null)}}
 */
const useMfeFeatureFlag = (mfeId) => {
  const flagKey = mfeConfig[mfeId]?.featureFlag || 'none';
  const useFlag = FLAG_HOOKS[flagKey] || useAlwaysEnabled;
  return useFlag();
};

export default useMfeFeatureFlag;
